import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2 } from "lucide-react";
import { FileUploadZone } from "@/components/fleet/FileUploadZone";

interface Vehicle {
  id: string;
  plate: string;
  make: string;
  model: string;
}

interface Provider {
  id: string;
  name: string;
}

interface FuelLog {
  id: string;
  vehicle_id: string;
  station_id?: string;
  occurred_at: string;
  liters: number;
  price_unit: number | null;
  total_value: number | null;
  odometer: number | null;
  fuel_type: string;
}

export interface FuelLogFormData {
  vehicle_id: string;
  occurred_at: string;
  liters: string;
  price_unit: string;
  total_value: string;
  odometer: string;
  fuel_type: string;
  station_id: string;
}

interface FuelLogFormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fuelLog?: FuelLog | null;
  onSubmit: (data: FuelLogFormData) => void;
  isLoading?: boolean;
  vehicles?: Vehicle[];
  stations?: Provider[];
  invoiceFile?: File | null;
  setInvoiceFile?: (file: File | null) => void;
}

export function FuelLogFormSheet({
  open,
  onOpenChange,
  fuelLog,
  onSubmit,
  isLoading = false,
  vehicles = [],
  stations = [],
  invoiceFile,
  setInvoiceFile,
}: FuelLogFormSheetProps) {
  const [formData, setFormData] = useState<FuelLogFormData>({
    vehicle_id: "",
    occurred_at: "",
    liters: "",
    price_unit: "",
    total_value: "",
    odometer: "",
    fuel_type: "gasoline",
    station_id: "",
  });

  useEffect(() => {
    if (fuelLog) {
      setFormData({
        vehicle_id: fuelLog.vehicle_id,
        occurred_at: fuelLog.occurred_at,
        liters: fuelLog.liters.toString(),
        price_unit: fuelLog.price_unit?.toString() || "",
        total_value: fuelLog.total_value?.toString() || "",
        odometer: fuelLog.odometer?.toString() || "",
        fuel_type: fuelLog.fuel_type,
        station_id: fuelLog.station_id || "",
      });
    } else {
      setFormData({
        vehicle_id: "",
        occurred_at: "",
        liters: "",
        price_unit: "",
        total_value: "",
        odometer: "",
        fuel_type: "gasoline",
        station_id: "",
      });
    }
  }, [fuelLog, open]);

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    onSubmit(formData);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:w-[550px] p-0">
        <SheetHeader className="px-6 pt-6 pb-4 border-b">
          <SheetTitle>
            {fuelLog ? "Editar Abastecimento" : "Novo Abastecimento"}
          </SheetTitle>
          <SheetDescription>
            {fuelLog
              ? "Edite os dados do abastecimento selecionado."
              : "Preencha os dados para registrar um novo abastecimento."}
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-180px)] px-6 py-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <h4 className="text-sm font-medium border-b pb-2">Dados Básicos</h4>

              <div className="space-y-2">
                <Label htmlFor="vehicle_id">Veículo</Label>
                <Select
                  value={formData.vehicle_id}
                  onValueChange={(value) =>
                    setFormData({ ...formData, vehicle_id: value })
                  }
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um veículo" />
                  </SelectTrigger>
                  <SelectContent>
                    {vehicles?.map((vehicle) => (
                      <SelectItem key={vehicle.id} value={vehicle.id}>
                        {vehicle.plate} - {vehicle.make} {vehicle.model}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="station_id">
                  Posto de Combustível (Opcional)
                </Label>
                <Select
                  value={formData.station_id || undefined}
                  onValueChange={(value) =>
                    setFormData({ ...formData, station_id: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Nenhum posto selecionado" />
                  </SelectTrigger>
                  <SelectContent>
                    {stations?.map((station) => (
                      <SelectItem key={station.id} value={station.id}>
                        {station.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="occurred_at">Data e Hora</Label>
                <Input
                  id="occurred_at"
                  type="datetime-local"
                  value={formData.occurred_at}
                  onChange={(e) =>
                    setFormData({ ...formData, occurred_at: e.target.value })
                  }
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="liters">Litros</Label>
                  <Input
                    id="liters"
                    type="number"
                    step="0.01"
                    value={formData.liters}
                    onChange={(e) =>
                      setFormData({ ...formData, liters: e.target.value })
                    }
                    placeholder="0.00"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="total_value">Valor Total (R$)</Label>
                  <Input
                    id="total_value"
                    type="number"
                    step="0.01"
                    value={formData.total_value}
                    onChange={(e) =>
                      setFormData({ ...formData, total_value: e.target.value })
                    }
                    placeholder="150.00"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="fuel_type">Tipo de Combustível</Label>
                  <Select
                    value={formData.fuel_type}
                    onValueChange={(value) =>
                      setFormData({ ...formData, fuel_type: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gasoline">Gasolina</SelectItem>
                      <SelectItem value="ethanol">Etanol</SelectItem>
                      <SelectItem value="diesel">Diesel</SelectItem>
                      <SelectItem value="gnv">GNV</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="odometer">Odômetro (km)</Label>
                  <Input
                    id="odometer"
                    type="number"
                    value={formData.odometer}
                    onChange={(e) =>
                      setFormData({ ...formData, odometer: e.target.value })
                    }
                    placeholder="12345"
                  />
                </div>
              </div>
            </div>

            {setInvoiceFile && (
              <div className="space-y-4">
                <h4 className="text-sm font-medium border-b pb-2">Anexos</h4>
                <div className="space-y-2">
                  <Label>Nota Fiscal</Label>
                  <FileUploadZone
                    onFileSelect={setInvoiceFile}
                    selectedFile={invoiceFile}
                  />
                </div>
              </div>
            )}
          </form>
        </ScrollArea>

        <SheetFooter className="px-6 py-4 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancelar
          </Button>
          <Button type="submit" onClick={handleSubmit} disabled={isLoading}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {fuelLog ? "Atualizar" : "Cadastrar"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
